/**
 * LoopNest Media Provider — AI Generated (Canvas-based)
 * Generates original visuals using canvas — no external dependency.
 * License: LoopNest AI Generated (CC0-equivalent)
 * Used as fallback when external providers are unavailable.
 */

import { BaseMediaProvider, LICENSE_TYPES, createMediaItem } from './baseProvider';

export class AIGeneratedProvider extends BaseMediaProvider {
  constructor() {
    super({
      id: 'loopnest-generated',
      label: 'LoopNest AI Generated',
      license: LICENSE_TYPES?.AI_GENERATED,
      requiresAttribution: false,
      enabled: true,
    });
  }

  /**
   * Generate a canvas-based visual for a query
   * Returns a data URL
   */
  generateCanvasImage(query, width = 1280, height = 720, seed = 0) {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas?.getContext('2d');

      // Deterministic color from query + seed
      const hash = [...(query + seed)]?.reduce((acc, c) => acc + c?.charCodeAt(0), 0);
      const hue1 = hash % 360;
      const hue2 = (hue1 + 120) % 360;
      const hue3 = (hue1 + 240) % 360;

      // Gradient background
      const grad = ctx?.createLinearGradient(0, 0, width, height);
      grad?.addColorStop(0, `hsl(${hue1}, 60%, 8%)`);
      grad?.addColorStop(0.5, `hsl(${hue2}, 50%, 12%)`);
      grad?.addColorStop(1, `hsl(${hue3}, 55%, 6%)`);
      ctx.fillStyle = grad;
      ctx?.fillRect(0, 0, width, height);

      // Orbs
      for (let i = 0; i < 5; i++) {
        const ox = (Math.sin(hash * 0.01 + i * 1.3) * 0.5 + 0.5) * width;
        const oy = (Math.cos(hash * 0.01 + i * 0.9) * 0.5 + 0.5) * height;
        const or = 80 + (hash % 60) + i * 30;
        const orbGrad = ctx?.createRadialGradient(ox, oy, 0, ox, oy, or);
        const oh = (hue1 + i * 40) % 360;
        orbGrad?.addColorStop(0, `hsla(${oh}, 70%, 60%, 0.25)`);
        orbGrad?.addColorStop(1, 'transparent');
        ctx?.beginPath();
        ctx?.arc(ox, oy, or, 0, Math.PI * 2);
        ctx.fillStyle = orbGrad;
        ctx?.fill();
      }

      // Label
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx?.fillText(query?.slice(0, 40), width / 2, height - 20);

      return canvas?.toDataURL('image/jpeg', 0.85);
    } catch {
      return null;
    }
  }

  async search(query, options = {}) {
    const { width = 1280, height = 720, count = 5, seed = 0 } = options;
    const items = [];
    for (let i = 0; i < count; i++) {
      const dataUrl = this.generateCanvasImage(query, width, height, seed + i);
      if (dataUrl) {
        items?.push(createMediaItem({
          id: `ai-generated-${query}-${seed + i}`,
          url: dataUrl,
          previewUrl: dataUrl,
          alt: `AI generated: ${query}`,
          providerId: this.id,
          license: this.license,
          requiresAttribution: false,
          width,
          height,
        }));
      }
    }
    return items;
  }
}

export const aiGeneratedProvider = new AIGeneratedProvider();
